import React, { useEffect, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { Button, Checkbox, ModalBox } from '@fourthwall/components';
import { base64ToFile } from '@fourthwall/utils/dist/file';
import * as S from './GalleryImageUploader.styled';
import { getCroppedImage } from './GalleryImageUploader.utils';
const DEFAULT_ASPECT = 3 / 4;
const getConfirmLabel = (index, totalFiles, filesToUpload) => {
    // NOTE: we take into account skipped photos, that's why we add 1 to the filesToUpload count
    if (totalFiles > 1 && index === totalFiles - 1) {
        return `Add ${filesToUpload + 1} photos`;
    }
    return 'Add photo';
};
export const GalleryImageUploader = ({ images, uploadToAllVariantsVisible, onClose, uploadBlobs, }) => {
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const processedImagesRef = useRef([]);
    const [imageSize, setImageSize] = useState();
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [aspect, setAspect] = useState(DEFAULT_ASPECT);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState();
    const [uploadToAllSelected, setUploadToAllSelected] = useState(false);
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);
    const imageAspect = imageSize ? imageSize.naturalWidth / imageSize.naturalHeight : undefined;
    const handleZoomChange = (zoomValue) => setZoom(zoomValue);
    const handleCropComplete = (percentages, pixels) => setCroppedAreaPixels(pixels);
    const handleMediaLoad = (mediaSize) => {
        setAspect(DEFAULT_ASPECT);
        setImageSize(mediaSize);
    };
    const handleRatioCheckboxChange = (value) => {
        setAspect(value ? DEFAULT_ASPECT : imageAspect);
    };
    const handleUploadToAllChange = (value) => setUploadToAllSelected(value);
    const handleConfirm = async () => {
        const currentImage = images[activeImageIndex];
        const isOriginalImage = zoom === 1 && aspect === imageAspect;
        const processedImage = croppedAreaPixels && !isOriginalImage
            ? await getCroppedImage(currentImage[0], croppedAreaPixels, currentImage[1])
            : await base64ToFile(currentImage[0], 'image', currentImage[1]);
        const readyToUploadImage = {
            blob: processedImage,
            uploadToAllVariants: uploadToAllSelected,
        };
        processedImagesRef.current = [...processedImagesRef.current, readyToUploadImage];
        if (activeImageIndex === images.length - 1) {
            onClose();
            await uploadBlobs([...processedImagesRef.current]);
        }
        else {
            setActiveImageIndex((prevState) => prevState + 1);
        }
    };
    const handleSkip = async () => {
        if (activeImageIndex === images.length - 1) {
            if (processedImagesRef.current.length > 0) {
                await uploadBlobs([...processedImagesRef.current]);
            }
            onClose();
        }
        else {
            setActiveImageIndex((prevState) => prevState + 1);
        }
    };
    const currentImage = images[activeImageIndex] || ['', 'image/jpeg'];
    return (React.createElement(ModalBox, { title: images.length > 1
            ? `Upload new photo (${activeImageIndex + 1}/${images.length})`
            : 'Upload new photo', size: "large", onClose: onClose },
        React.createElement(S.CropperContainer, null,
            React.createElement(Cropper, { image: currentImage[0], crop: crop, zoom: zoom, aspect: aspect, showGrid: false, onCropChange: setCrop, onMediaLoaded: handleMediaLoad, style: {
                    cropAreaStyle: {
                        color: 'rgba(255,255,255,0.75)',
                    },
                }, onCropComplete: handleCropComplete, onZoomChange: handleZoomChange })),
        React.createElement(S.Footer, null,
            React.createElement(S.CheckboxContainer, null,
                imageSize && imageAspect !== DEFAULT_ASPECT && (React.createElement(Checkbox, { value: aspect === DEFAULT_ASPECT, label: "Crop to 3:4 aspect ratio (recommended)", onValueChange: handleRatioCheckboxChange })),
                uploadToAllVariantsVisible && (React.createElement(Checkbox, { value: uploadToAllSelected, label: "Upload to all variants", onValueChange: handleUploadToAllChange }))),
            React.createElement(S.ButtonContainer, null,
                images.length > 1 && React.createElement(Button, { label: "Skip", appearance: "secondary", onClick: handleSkip }),
                React.createElement(Button, { label: getConfirmLabel(activeImageIndex, images.length, processedImagesRef.current.length), appearance: "primary", onClick: handleConfirm })))));
};
